/**
 * Usage and spending (admins): what agents cost in a time frame, by agent, person, room,
 * project and kind, day by day, plus the coding workers' GitHub minutes.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router";
import { ErrorNote, Fold } from "../components/ui";
import { api, ApiError } from "../lib/api";
import { useStore } from "../lib/store";

interface Line {
  answers: number;
  checks: number;
  failed: number;
  input: number;
  output: number;
  cost: number;
  unpriced: number;
}
interface Usage {
  from: string;
  to: string;
  total: Line;
  plan: Line;
  byAgent: (Line & { id: string; name: string; kind: string; provider: string | null; model: string | null })[];
  byPerson: (Line & { id: string | null; name: string })[];
  byRoom: (Line & { id: string; name: string })[];
  byProject: (Line & { id: string | null; name: string })[];
  byKind: (Line & { kind: string })[];
  byDay: (Line & { day: string })[];
  coding: { runs: number; minutes: number; list: { id: string; task: string; project: string; status: string; conclusion: string | null; minutes: number | null; pr: number | null; at: string }[] };
}

type Frame = "today" | "7d" | "30d" | "month" | "12m" | "custom";
const FRAMES: { id: Frame; label: string }[] = [
  { id: "today", label: "Today" },
  { id: "7d", label: "Last 7 days" },
  { id: "30d", label: "Last 30 days" },
  { id: "month", label: "This month" },
  { id: "12m", label: "Last 12 months" },
  { id: "custom", label: "Dates" },
];
const KIND_LABEL: Record<string, string> = { mention: "Answers to a mention", round: "@agents round tables", host: "Host answers", check: "Host checks (is it for the agents?)", draft: "Event agent drafts", review: "Pull request reviews (Coordinator)" };
const PLAN_KINDS = new Set(["claude_channel", "codex_cli"]);

const money = (n: number) => (n === 0 ? "$0" : n < 0.01 ? `$${n.toFixed(4)}` : `$${n.toFixed(2)}`);
const tokens = (n: number) => (n >= 1_000_000 ? `${(n / 1_000_000).toFixed(2)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));
const dateInput = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function range(frame: Frame, custom: { from: string; to: string }): { from: Date; to: Date } {
  const now = new Date();
  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (frame === "today") return { from: midnight, to: now };
  if (frame === "7d") return { from: new Date(midnight.getTime() - 6 * 86_400_000), to: now };
  if (frame === "30d") return { from: new Date(midnight.getTime() - 29 * 86_400_000), to: now };
  if (frame === "month") return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: now };
  if (frame === "12m") return { from: new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()), to: now };
  const [fy, fm, fd] = custom.from.split("-").map(Number);
  const [ty, tm, td] = custom.to.split("-").map(Number);
  return { from: new Date(fy!, fm! - 1, fd!), to: new Date(ty!, tm! - 1, td! + 1) };
}

export function UsagePage() {
  const boot = useStore((s) => s.boot)!;
  const [frame, setFrame] = useState<Frame>(() => {
    try {
      return (localStorage.getItem("agora.usageFrame") as Frame) || "30d";
    } catch {
      return "30d";
    }
  });
  const today = dateInput(new Date());
  const [custom, setCustom] = useState({ from: dateInput(new Date(Date.now() - 13 * 86_400_000)), to: today });
  const [data, setData] = useState<Usage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const r = useMemo(() => range(frame, custom), [frame, custom]);

  useEffect(() => {
    try {
      localStorage.setItem("agora.usageFrame", frame);
    } catch {
      // storage blocked
    }
    setError(null);
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    api<Usage>(`/api/settings/usage?from=${encodeURIComponent(r.from.toISOString())}&to=${encodeURIComponent(r.to.toISOString())}&tz=${encodeURIComponent(tz)}`).then(setData, (err) =>
      setError(err instanceof ApiError ? err.message : "Could not load the usage."),
    );
  }, [r, frame]);

  if (boot.workspace.role !== "admin") return <Navigate to="/" replace />;
  const t = data?.total;
  const api$ = t ? t.cost : 0;
  const maxDay = Math.max(0.000001, ...(data?.byDay.map((d) => d.cost) ?? [0]));

  return (
    <main className="page usage-page">
      <p className="crumbs">
        <Link to="/settings">Settings</Link>
      </p>
      <h1>Usage and spending</h1>
      <p className="lead">What agents cost on API keys, and what runs on your plans (Claude and ChatGPT: tokens counted, no API cost). Costs are estimates from the prices set for each agent.</p>

      <div className="frame-bar" role="tablist" aria-label="Time frame">
        {FRAMES.map((f) => (
          <button key={f.id} type="button" role="tab" aria-selected={frame === f.id} className={frame === f.id ? "on" : ""} onClick={() => setFrame(f.id)}>
            {f.label}
          </button>
        ))}
        {frame === "custom" && (
          <span className="frame-dates">
            <input type="date" value={custom.from} max={custom.to} onChange={(e) => e.target.value && setCustom({ ...custom, from: e.target.value })} aria-label="From" />
            <span>to</span>
            <input type="date" value={custom.to} min={custom.from} max={today} onChange={(e) => e.target.value && setCustom({ ...custom, to: e.target.value })} aria-label="To" />
          </span>
        )}
      </div>
      <ErrorNote error={error} />
      {!data && !error && <p className="muted-text">Loading</p>}
      {data && t && (
        <>
          <div className="usage-tiles">
            <div>
              <small>API cost, estimated</small>
              <b>{money(api$)}</b>
              {t.unpriced > 0 && <small className="muted-text">{t.unpriced} answers without prices: set them in Settings, Agents.</small>}
            </div>
            <div>
              <small>Tokens in / out</small>
              <b>
                {tokens(t.input)} / {tokens(t.output)}
              </b>
            </div>
            <div>
              <small>Answers</small>
              <b>{t.answers}</b>
              <small className="muted-text">
                {t.checks} host checks{t.failed ? `, ${t.failed} failed` : ""}
              </small>
            </div>
            <div>
              <small>On your plans (no API cost)</small>
              <b>{data.plan.answers} answers</b>
              <small className="muted-text">
                {tokens(data.plan.input)} / {tokens(data.plan.output)} tokens
              </small>
            </div>
            <div>
              <small>Coding workers</small>
              <b>{data.coding.runs} runs</b>
              <small className="muted-text">{data.coding.minutes} GitHub minutes</small>
            </div>
          </div>

          <Fold id="usage-days" title="Day by day" summary={`${data.byDay.length} days with activity, ${money(api$)} in all.`}>
            {data.byDay.length ? (
              <div className="day-bars" role="img" aria-label="API cost per day">
                {data.byDay.map((d) => (
                  <div key={d.day} className="day-bar" title={`${d.day}: ${money(d.cost)}, ${d.answers} answers, ${tokens(d.input + d.output)} tokens`}>
                    <span style={{ height: `${Math.max(2, (d.cost / maxDay) * 100)}%` }} />
                    <small>{d.day.slice(5)}</small>
                  </div>
                ))}
              </div>
            ) : (
              <p className="muted-text">No agent activity in this time frame.</p>
            )}
          </Fold>

          <UsageTable id="usage-agents" title="By agent" rows={data.byAgent.map((a) => ({ ...a, label: a.name, note: PLAN_KINDS.has(a.kind) ? "on your plan" : [a.provider, a.model].filter(Boolean).join(" · "), plan: PLAN_KINDS.has(a.kind) }))} />
          <UsageTable id="usage-people" title="By person who asked" rows={data.byPerson.map((p) => ({ ...p, label: p.name, note: "" }))} />
          <UsageTable id="usage-projects" title="By project" rows={data.byProject.map((p) => ({ ...p, label: p.name, note: "" }))} />
          <UsageTable id="usage-rooms" title="By room" rows={data.byRoom.map((x) => ({ ...x, label: `#${x.name}`, note: "" }))} />
          <UsageTable id="usage-kinds" title="By kind" rows={data.byKind.map((k) => ({ ...k, label: KIND_LABEL[k.kind] ?? k.kind, note: "" }))} />

          <Fold id="usage-coding" title="Coding workers" summary={`${data.coding.runs} runs, ${data.coding.minutes} GitHub minutes.`}>
            <p className="muted-text">Claude Code on your Max plan, in GitHub's runners: no token cost. GitHub counts the minutes (2,000 a month free on GitHub Free, 3,000 on Pro, then about $0.006 a minute; public repos are free).</p>
            {data.coding.list.length ? (
              <div className="table-wrap">
                <table className="usage-table">
                  <thead>
                    <tr>
                      <th>Task</th>
                      <th>Project</th>
                      <th>Result</th>
                      <th className="n">Minutes</th>
                      <th>When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.coding.list.map((c) => (
                      <tr key={c.id}>
                        <td>{c.task}</td>
                        <td>{c.project}</td>
                        <td>
                          {c.conclusion ?? c.status}
                          {c.pr ? `, PR #${c.pr}` : ""}
                        </td>
                        <td className="n">{c.minutes ?? ""}</td>
                        <td>{new Date(c.at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="muted-text">No coding worker ran in this time frame.</p>
            )}
          </Fold>
        </>
      )}
    </main>
  );
}

function UsageTable({ id, title, rows }: { id: string; title: string; rows: (Line & { label: string; note: string; plan?: boolean })[] }) {
  const total = rows.reduce((n, r) => n + r.cost, 0);
  return (
    <Fold id={id} title={title} summary={rows.length ? `${rows.length} ${rows.length === 1 ? "line" : "lines"}, top: ${rows[0]!.label} ${money(rows[0]!.cost)}.` : "Nothing in this time frame."}>
      {rows.length ? (
        <div className="table-wrap">
          <table className="usage-table">
            <thead>
              <tr>
                <th>Name</th>
                <th className="n">Answers</th>
                <th className="n">Checks</th>
                <th className="n">Tokens in</th>
                <th className="n">Tokens out</th>
                <th className="n">Cost</th>
                <th className="n">Share</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.label}-${i}`}>
                  <td>
                    {r.label}
                    {r.note && <small className="muted-text"> {r.note}</small>}
                  </td>
                  <td className="n">{r.answers}</td>
                  <td className="n">{r.checks || ""}</td>
                  <td className="n">{tokens(r.input)}</td>
                  <td className="n">{tokens(r.output)}</td>
                  <td className="n">
                    {r.plan ? "plan" : money(r.cost)}
                    {r.unpriced > 0 && !r.plan ? "*" : ""}
                  </td>
                  <td className="n">{total > 0 && !r.plan ? `${Math.round((r.cost / total) * 100)}%` : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.some((r) => r.unpriced > 0 && !r.plan) && <small className="muted-text">* some answers have no price set, so the cost is higher than shown.</small>}
        </div>
      ) : (
        <p className="muted-text">Nothing in this time frame.</p>
      )}
    </Fold>
  );
}
